import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, ChevronDown, TrendingUp, ShoppingCart, MousePointer, FlaskConical, MessageSquare, Layout, Layers as LayersIcon, Map, BarChart3, Puzzle, Sparkles, Route } from "lucide-react";
import { useState, useEffect } from "react";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import logo from "@/assets/logo.png";

const useCases = [
  { icon: TrendingUp, title: "Increase Conversions", href: "/use-cases/conversion" },
  { icon: ShoppingCart, title: "Recover Carts", href: "/use-cases/cart-recovery" },
  { icon: MousePointer, title: "Personalize Content", href: "/use-cases/content-personalization" },
  { icon: FlaskConical, title: "AI A/B Testing", href: "/use-cases/ab-testing" },
];

const widgets = [
  { icon: MessageSquare, title: "Popups", href: "/widgets/popups" },
  { icon: Layout, title: "Banners", href: "/widgets/banners" },
  { icon: LayersIcon, title: "Social Proof", href: "/widgets/social-proof" },
];

const features = [
  { icon: Map, title: "Segmentation", href: "/features/segmentation" },
  { icon: BarChart3, title: "Analytics", href: "/features/analytics" },
  { icon: Puzzle, title: "Integrations", href: "/features/integrations" },
  { icon: Sparkles, title: "AI", href: "/features/ai" },
  { icon: Route, title: "AI Journeys", href: "/features/journeys" },
  { icon: MessageSquare, title: "Popups", href: "/widgets/popups" },
  { icon: Layout, title: "Banners", href: "/widgets/banners" },
  { icon: LayersIcon, title: "Social Proof", href: "/widgets/social-proof" },
];

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openMobileSection, setOpenMobileSection] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const toggleMobileSection = (section: string) => {
    setOpenMobileSection(openMobileSection === section ? null : section);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b transition-all duration-300 ${
        scrolled
          ? "bg-background/90 border-border shadow-md"
          : "bg-background/80 border-border"
      }`}
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" aria-label="Superdynamic - Home">
            <img src={logo} alt="Superdynamic" className="h-8" width={128} height={32} />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center">
            <NavigationMenu>
              <NavigationMenuList className="gap-0">
                {/* Product Mega Menu */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="bg-transparent hover:bg-muted text-muted-foreground hover:text-foreground data-[state=open]:bg-muted text-sm">
                    Product
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="w-[240px] p-4">
                      <ul className="space-y-1">
                        {useCases.map((item) => (
                          <li key={item.title}>
                            <NavigationMenuLink asChild>
                              <Link to={item.href} className="flex items-center gap-2 p-2 rounded-md hover:bg-muted transition-colors text-sm text-muted-foreground hover:text-foreground">
                                <item.icon className="w-4 h-4" />
                                {item.title}
                              </Link>
                            </NavigationMenuLink>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* Features */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="bg-transparent hover:bg-muted text-muted-foreground hover:text-foreground data-[state=open]:bg-muted text-sm">
                    Features
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="w-[320px] p-6 grid grid-cols-1 gap-4">
                      <div>
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">Platform</p>
                        <ul className="space-y-1">
                          {features.filter(f => !widgets.some(w => w.href === f.href)).map((item) => (
                            <li key={item.title}>
                              <NavigationMenuLink asChild>
                                <Link to={item.href} className="flex items-center gap-2 p-2 rounded-md hover:bg-muted transition-colors text-sm text-muted-foreground hover:text-foreground">
                                  <item.icon className="w-4 h-4" />
                                  {item.title}
                                </Link>
                              </NavigationMenuLink>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">Widgets</p>
                        <ul className="space-y-1">
                          {widgets.map((item) => (
                            <li key={item.title}>
                              <NavigationMenuLink asChild>
                                <Link to={item.href} className="flex items-center gap-2 p-2 rounded-md hover:bg-muted transition-colors text-sm text-muted-foreground hover:text-foreground">
                                  <item.icon className="w-4 h-4" />
                                  {item.title}
                                </Link>
                              </NavigationMenuLink>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link to="/resources/blog" className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-muted inline-flex items-center">
                      Blog
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link to="/pricing" className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-muted inline-flex items-center">
                      Pricing
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-2">
            <Button asChild size="sm" className="rounded-full px-6">
              <Link to="/pricing">Join Waitlist</Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-foreground"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden pt-4 pb-2 space-y-1" role="menu">
            <button
              onClick={() => toggleMobileSection('product')}
              className="flex items-center justify-between w-full px-4 py-2 text-foreground hover:bg-muted rounded-lg transition-colors"
              aria-expanded={openMobileSection === 'product'}
            >
              <span>Product</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${openMobileSection === 'product' ? 'rotate-180' : ''}`} />
            </button>
            {openMobileSection === 'product' && (
              <div className="pl-4 space-y-1">
                <p className="px-4 py-1 text-xs text-muted-foreground uppercase">Use Cases</p>
                {useCases.map((item) => (
                  <Link key={item.title} to={item.href} className="flex items-center gap-2 px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors" onClick={() => setIsMenuOpen(false)}>
                    <item.icon className="w-4 h-4" />
                    {item.title}
                  </Link>
                ))}
              </div>
            )}

            <button
              onClick={() => toggleMobileSection('features')}
              className="flex items-center justify-between w-full px-4 py-2 text-foreground hover:bg-muted rounded-lg transition-colors"
              aria-expanded={openMobileSection === 'features'}
            >
              <span>Features</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${openMobileSection === 'features' ? 'rotate-180' : ''}`} />
            </button>
            {openMobileSection === 'features' && (
              <div className="pl-4 space-y-1">
                {features.map((item) => (
                  <Link key={item.title} to={item.href} className="flex items-center gap-2 px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors" onClick={() => setIsMenuOpen(false)}>
                    <item.icon className="w-4 h-4" />
                    {item.title}
                  </Link>
                ))}
              </div>
            )}

            <Link to="/resources/blog" className="block px-4 py-2 text-foreground hover:bg-muted rounded-lg transition-colors" onClick={() => setIsMenuOpen(false)}>Blog</Link>
            <Link to="/pricing" className="block px-4 py-2 text-foreground hover:bg-muted rounded-lg transition-colors" onClick={() => setIsMenuOpen(false)}>Pricing</Link>

            <div className="flex flex-col gap-2 pt-4 border-t border-border mt-2">
              <Button asChild className="w-full rounded-full">
                <Link to="/pricing">Join Waitlist</Link>
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
